import { makeObservable, observable, computed, action, flow, toJS } from "mobx";
import * as richModalChatService from "../../services/richmodal";
import { CHAT_PAGE_SIZE } from "./constants";
import { ResCode } from "../../constants/request";
import Chat from "./model";
import {
  ESessionFileType,
  ESessionStatus,
  ISessionFile,
} from "../session/type";
import {
  EChatType,
  EStreamDataStatus,
  IChatMessage,
  IChatSendMessage,
  ESendMsgType,
  EFileQueryType,
  EChatStatus,
  IChatRecvMessage,
  EChatModel,
} from "./types";
import { deleteFileQueryMsg } from "../../services/richmodal";
import { isImageFileName } from "../file/utils";

class Manager {
  @observable isCreatingQA = false;
  @observable isFetchingList = false;
  @observable session_id: string;
  @observable session_status?: ESessionStatus;
  @observable.ref chatInstanceList: Chat[] = [];
  @observable chatHistoryData: IChatMessage[] = [];

  constructor(session_id: string, session_status?: ESessionStatus) {
    makeObservable(this);
    this.setIsCreatingQA(false);
    this.setIsFetchingList(false);
    this.session_id = session_id;
    this.session_status = session_status;
  }

  @computed
  get keyPointMap() {
    return this.chatInstanceList.reduce<{
      [pro: string]: (IChatSendMessage & { msgId?: string })[];
    }>((pre, item) => {
      const fileMsg = item.send_msg?.find(
        msg => msg.msg_type === ESendMsgType.file_query,
      );
      if (
        fileMsg &&
        fileMsg.file_query?.file_query_type === EFileQueryType.key_point
      ) {
        if (pre[fileMsg.file_query!.file_id]) {
          pre[fileMsg.file_query!.file_id].push({
            ...fileMsg,
            msgId: item.message_id,
          });
        } else {
          pre[fileMsg.file_query!.file_id] = [
            {
              ...fileMsg,
              msgId: item.message_id,
            },
          ];
        }
      }
      return pre;
    }, {});
  }

  @computed
  get _firstMessageId(): string {
    return this.chatInstanceList.length
      ? this.chatInstanceList[0].message_id!
      : "";
  }

  @computed
  get _lastMessageId(): string {
    return this.chatInstanceList.length
      ? this.chatInstanceList[this.chatInstanceList.length - 1].message_id!
      : "0";
  }

  @action
  setIsCreatingQA(v: boolean) {
    this.isCreatingQA = v;
  }

  @action
  setIsFetchingList(v: boolean) {
    this.isFetchingList = v;
  }

  @action
  setChatInstanceList(v: Chat[]) {
    this.chatInstanceList = v;
  }

  @action.bound
  setSessionId(value: string) {
    this.session_id = value;
  }

  @action.bound
  setSessionStatus(value: ESessionStatus) {
    this.session_status = value;
  }

  @action.bound
  addChat(
    msg: IChatSendMessage[],
    files?: ISessionFile[],
    guess?: boolean,
    chatModel?: EChatModel,
    web_search?: boolean,
  ) {
    const newChat = new Chat(
      {
        type: EChatType.stream,
        streamingStatus: EStreamDataStatus.init,
        send_msg: msg,
        files,
        recv_msg: null,
        current_msg: null,
        cached_msg: null,
        session_id: this.session_id,
        session_status: this.session_status,
        parent_id: this._lastMessageId,
        status: EChatStatus.work,
        guess: guess,
        chatModel,
        web_search,
        // onSessionIdChange: this.setSessionId,
        // onSessionStatusChange: this.setSessionStatus,
      },
      this.setSessionId,
      this.setSessionStatus,
      this.handleStreamEnd,
    );
    this.chatInstanceList = [...this.chatInstanceList, newChat];

    return newChat;
  }

  handleStreamEnd(v: Chat) {
    console.log(" handleStreamEnd1", v);
  }

  @action.bound
  addQAChat(
    message_id: string,
    sendMsg: IChatSendMessage[],
    recvMsg: IChatRecvMessage[],
  ) {
    this.chatInstanceList = [
      ...this.chatInstanceList,
      new Chat(
        {
          type: EChatType.history,
          streamingStatus: EStreamDataStatus.streamingEnd,
          send_msg: sendMsg,
          recv_msg: recvMsg,
          current_msg: null,
          cached_msg: null,
          message_id,
          session_id: this.session_id,
          session_status: this.session_status,
          parent_id: this._lastMessageId,
          status: EChatStatus.work,
        },
        this.setSessionId,
        this.setSessionStatus,
      ),
    ];
  }

  async getInfoAfterCreateQA(params: {
    parent_id: string;
    send_msg: IChatSendMessage[];
    recv_msg: IChatRecvMessage[];
  }) {
    if (this.isCreatingQA) return null;
    try {
      this.setIsCreatingQA(true);
      const res = await richModalChatService.createQAMsg(params);
      if (res.code === ResCode.Success) {
        const { message_id, session_id } = res.message || {};
        return {
          message_id,
          session_id,
        };
      } else {
        throw new Error(JSON.stringify(res));
      }
    } catch (error) {
      return null;
    } finally {
      this.setIsCreatingQA(false);
    }
  }

  @action
  deleteChat(msgId: string) {
    const chatIndex = this.chatInstanceList.findIndex(
      item => item.message_id === msgId,
    );
    const chat = this.chatInstanceList[chatIndex];
    const fileQuery = chat?.send_msg?.find(
      it => it?.file_query?.file_query_type === EFileQueryType.key_point,
    );
    // 现在只能删除划重点的message
    if (fileQuery) {
      deleteFileQueryMsg(fileQuery.file_query!.file_id, msgId).then(res => {
        if (res.code === 0) {
          const list = [...this.chatInstanceList];
          list.splice(chatIndex, 1);
          this.setChatInstanceList(list);
        }
      });
    }
  }

  async getChatInstanceList(page_size?: number) {
    this.setIsFetchingList(true);
    const currentList = await this.getChatHistoryData(page_size);
    const appendList: Chat[] = currentList.map((item: IChatMessage) => {
      return new Chat({
        ...item,
        chatModel: item.client_chan as EChatModel,
        type: EChatType.history,
        session_id: this.session_id,
        session_status: this.session_status,
        streamingStatus: EStreamDataStatus.streamingEnd,
        files: item.files?.map(file => ({
          ...file,
          type:
            file.type ||
            (isImageFileName(file.file_name)
              ? ESessionFileType.image
              : ESessionFileType.file),
        })),
      });
    });
    this.setChatInstanceList([...appendList, ...this.chatInstanceList]);
    this.setIsFetchingList(false);
    return appendList.length > 0;
  }

  async getChatHistoryData(page_size?: number) {
    try {
      const { code, message } = await richModalChatService.getChatList({
        session_id: this.session_id,
        page_size: page_size || CHAT_PAGE_SIZE,
        next: this._firstMessageId,
      });
      const resList = code === ResCode.Success ? message : [];
      this.chatHistoryData.unshift(...resList);
      return resList;
    } catch (error) {
      !this.chatHistoryData && (this.chatHistoryData = []);
      console.log(`getChatDataList error`, error);
      return [];
    }
  }
}

export default Manager;
